import { GenerateIcon } from '@/components/Icons';
import { useInstructionShow, useNewUserFlowStartShow } from '@/state/show-store';
import { FileCheck2Icon, FileMusicIcon, KeyboardMusicIcon, ListTreeIcon, TablePropertiesIcon, TextCursorInputIcon } from 'lucide-react';
import React from 'react';
import Joyride, { CallBackProps, Placement, Step, TooltipRenderProps } from 'react-joyride';
import { PromptCopyList } from '../NewUserPage';
import './index.css';

function CustomTooltip(props: TooltipRenderProps & { ExtraComp?: React.ReactElement }) {
  const { index, size, primaryProps, skipProps, step, tooltipProps, ExtraComp } = props;
  return (
    <div
      className=" bg-[#2B2D2F] overflow-hidden rounded-xl text-jen-background-tertiary flex justify-between gap-4"
      {...tooltipProps}
      style={{
        boxShadow:
          '176px 223px 80px 0px rgba(0, 0, 0, 0.01), 113px 143px 73px 0px rgba(0, 0, 0, 0.04), 63px 80px 61px 0px rgba(0, 0, 0, 0.15), 28px 36px 45px 0px rgba(0, 0, 0, 0.26), 7px 9px 25px 0px rgba(0, 0, 0, 0.29)',
      }}
    >
      <div className="p-4 flex justify-between gap-4">
        <div>{step.data.icon}</div>

        <div className="">
          {step.title && <div className=" text-jen-size-heading-03 mb-[5px] w-[214px]">{step.title}</div>}
          <div className="tooltip__content w-[214px] text-[12px] leading-[14px] tracking-[-0.36px]">{step.content}</div>
          <div className="tooltip__footer mt-[20px] text-[12px] flex justify-between items-center">
            <div>
              <button
                className="p-[10px] mr-[9px] opacity-90 hover:opacity-100"
                {...primaryProps}
                style={{
                  borderRadius: '4px',
                  border: ' 1px solid rgba(255, 91, 39, 0.80)',
                  background: 'linear-gradient(155deg, #FF5B27 18.44%, #993717 109.43%)',
                  boxShadow:
                    '19px 24px 8px 0px rgba(0, 0, 0, 0.00), 12px 15px 8px 0px rgba(0, 0, 0, 0.01), 7px 9px 7px 0px rgba(0, 0, 0, 0.05), 3px 4px 5px 0px rgba(0, 0, 0, 0.09), 1px 1px 3px 0px rgba(0, 0, 0, 0.10)',
                }}
              >
                Got it
              </button>
              <button className="text-[#9d9da6]  opacity-90 hover:opacity-100" {...skipProps}>
                Skip all
              </button>
            </div>
            <div className="text-[#9d9da6]">
              {index + 1}/{size}
            </div>
          </div>
        </div>
      </div>

      {ExtraComp}
    </div>
  );
}

export const GenerateInstruction = () => {
  const { isShow, setIsShow } = useInstructionShow();
  const steps: Step[] = [
    {
      target: '.track_list_box',
      disableBeacon: true,
      title: 'Here’s your new track',
      placement: 'bottom' as Placement,
      data: {
        icon: <FileMusicIcon color="#9d9da6" />,
      },
      content: 'Congrats on making your first track. All your tracks will appear here in your workstation.',
    },
    {
      target: '.track_name_box',
      disableBeacon: true,
      title: 'Your track details',
      placement: 'left-start' as Placement,
      data: {
        icon: <TablePropertiesIcon color="#9d9da6" />,
      },
      content:
        'Selecting a track in your workstation will open the details view. You can also make changes such as extending, trimming and refilling.',
    },
    {
      target: '.track_control_box',
      disableBeacon: true,
      title: 'Perfect your track',
      placement: 'top' as Placement,
      data: {
        icon: <FileCheck2Icon color="#9d9da6" />,
      },
      content: 'Refine your track by extending, trimming and even refilling sections to get it just how you need it.',
    },

    {
      target: '.catalog_box',
      disableBeacon: true,
      title: 'Keep your tracks organized',
      placement: 'right-end' as Placement,
      data: {
        icon: <ListTreeIcon color="#9d9da6" />,
      },
      content:
        'All your tracks will be automatically placed in ‘All Tracks’ and ‘Recent’. Further arrange your collection by creating Folders.',
    },
    {
      target: '.prompt_input_box',
      disableBeacon: true,
      title: 'Start making more',
      placement: 'top-start' as Placement,
      data: {
        icon: <KeyboardMusicIcon color="#9d9da6" />,
      },
      content: 'It’s time to get started. Is it going to be a chill lo-fi hip-hop beat? Or how about an intense heavy metal song?',
    },
  ];
  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status } = data;

    if (status === 'finished' || status === 'ready') {
      setIsShow(false);
    }
  };
  return (
    <Joyride
      tooltipComponent={CustomTooltip}
      steps={steps}
      callback={handleJoyrideCallback}
      run={isShow}
      // disableOverlay
      styles={{
        options: {
          arrowColor: '#2B2D2F',
        },
        tooltipTitle: {
          width: '100px',
          whiteSpace: 'normal',
        },
      }}
    />
  );
};

export const NewUserInstruction = () => {
  const { isShow, setIsShow } = useNewUserFlowStartShow();
  const steps: Step[] = [
    {
      target: '.prompt_input_box',
      disableBeacon: true,
      title: 'You‘re about to make your first track',
      placement: 'top-start' as Placement,
      data: {
        icon: <TextCursorInputIcon color="#9d9da6" />,
      },
      tooltipComponent: (props) => {
        return <CustomTooltip {...props} ExtraComp={<PromptCopyList />} />;
      },
      content: "Describe your track in Generator Bar below or pick one of the prompts we've spun up for you.",
    },
    {
      target: '.generator_box',
      disableBeacon: true,
      title: 'Jenerate your first track',
      placement: 'top-start' as Placement,
      data: {
        icon: <GenerateIcon color="#9d9da6" />,
      },
      content:
        'Once you have your prompt click here to jenerate. PRO TIP: Use the arrows in increase the number of generations you get each time.',
    },
  ];
  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status } = data;

    if (status === 'finished' || status === 'ready') {
      setIsShow(false);
    }
  };
  return (
    <Joyride
      tooltipComponent={CustomTooltip}
      steps={steps}
      callback={handleJoyrideCallback}
      run={isShow}
      // disableOverlay
      styles={{
        options: {
          arrowColor: '#2B2D2F',
        },
      }}
    />
  );
};
